import { Fragment, ReactNode } from 'react';

import { CardStack } from '../Applicant.StyledComponents';
import { Applicant } from '../Applicant.types';
import { ApplicantInformation } from './ApplicantInformation';
import { BusinessInformation } from './BusinessInformation';
import { CarrierInformation } from './CarrierInformation';
import { ContactInformation } from './ContactInformation';
import { FuelInformation } from './FuelInformation';
import { LoadPayInformation } from './LoadPayInformation';
import { OwnerInformation } from './OwnerInformation';

export interface ApplicationSection {
    key: string;
    order: number;
    node: ReactNode;
}

const FUEL_SECTION_ORDER = Number.MAX_SAFE_INTEGER;

// Only the sections the applicant holds, so a compare can leave a gap where the other side has one.
export const getApplicationSections = (
    applicant: Applicant,
    isSubmission = false,
): ApplicationSection[] => {
    const lastPageVisited = applicant.lastPageVisiteds?.[0]?.path ?? '';

    return [
        {
            key: 'applicant',
            order: 0,
            node: (
                <ApplicantInformation
                    id={applicant.id}
                    partnerId={applicant.faasPartnerId}
                    email={applicant.email}
                    phone={applicant.phone}
                    referredBy={applicant.referredBy}
                    lastPageVisited={lastPageVisited}
                    isSubmission={isSubmission}
                />
            ),
        },
        ...(applicant.loadPayProduct
            ? [
                  {
                      key: 'loadpay',
                      order: 1,
                      node: (
                          <LoadPayInformation applicant={applicant} isSubmission={isSubmission} />
                      ),
                  },
              ]
            : []),
        {
            key: 'business',
            order: 2,
            node: (
                <BusinessInformation
                    {...applicant.businessInfo}
                    isBusinessAPerson={applicant.isBusinessAPerson}
                    isSubmission={isSubmission}
                />
            ),
        },
        { key: 'carrier', order: 3, node: <CarrierInformation {...applicant.carrierInfo} /> },
        { key: 'contact', order: 4, node: <ContactInformation {...applicant.contactInfo} /> },
        // Keyed by position, as the compare matches owners: "Owner 1" against "Owner 1".
        ...(applicant.owners ?? []).map((owner, index) => ({
            key: `owner-${index + 1}`,
            order: 5 + index,
            node: (
                <OwnerInformation
                    {...owner}
                    subTitle={`Owner ${index + 1}`}
                    isSubmission={isSubmission}
                />
            ),
        })),
        ...(applicant.tcsFuelCardProduct
            ? [
                  {
                      key: 'fuel',
                      order: FUEL_SECTION_ORDER,
                      node: <FuelInformation applicant={applicant} />,
                  },
              ]
            : []),
    ];
};

interface ApplicationViewProps {
    applicant: Applicant;
    // A recorded submission, so cards show only what it holds and none of today's account data.
    isSubmission?: boolean;
}

export const ApplicationView = ({ applicant, isSubmission = false }: ApplicationViewProps) => (
    <CardStack>
        {getApplicationSections(applicant, isSubmission).map(({ key, node }) => (
            <Fragment key={key}>{node}</Fragment>
        ))}
    </CardStack>
);
